// M13.3 — donut + 100% normalised stacked area for the social cohort's
// device mix.
//
// Used inside MobileSection. Two SVG primitives sharing the same data:
//   • Donut — current-period sum, compact ~140px square, lives beside
//     the section header. Uses d3-shape's pie + arc generators.
//   • Stacked area — per-day mix over the window, 100% normalised so the
//     bands always sum to 100%. Uses d3-shape.stack() with stackOffsetExpand
//     (same primitive M13.1's SourceMixChart established).
//
// Device colour family — kept in sync with src/shared/report/device-mix-svg.ts
// so the dashboard, PDF, and shared viewer tell the same visual story:
//   Desktop = blurple-700  (#0E1680)
//   Mobile  = pink-500     (#EC4899) — the M12 marketing CTA family
//   Tablet  = gray-400     (#9CA3AF)
//   Other   = gray-200     (#E5E7EB)
//
// Layout note: MobileSection renders the donut on the right and the area
// chart on the left at ≥md, stacking on mobile. Donut + area generators
// are standalone components so the layout stays composable.

import { useEffect, useMemo, useRef, useState } from "react";
import { extent, max, sum } from "d3-array";
import { arc as d3Arc, pie as d3Pie } from "d3-shape";
import { scaleLinear, scaleTime } from "d3-scale";
import { area as d3Area, stack as d3Stack, stackOffsetExpand, type SeriesPoint } from "d3-shape";
import { DEVICE_COLOR, DEVICE_LABEL, DEVICE_RENDER_ORDER, type DeviceKey } from "./device-colors";

// ---------------------------------------------------------------------------
// Donut
// ---------------------------------------------------------------------------

export interface MobileDonutProps {
  perDevice: ReadonlyArray<{ device: DeviceKey; sessions: number }>;
  /** Donut viewBox edge in CSS pixels. Compact (~140px) by default so the
   *  chart sits in the right column without crowding the section header. */
  size?: number;
  ariaLabel?: string;
}

export function MobileDonut({
  perDevice,
  size = 140,
  ariaLabel = "Device mix donut",
}: MobileDonutProps) {
  const totalSessions = useMemo(
    () => perDevice.reduce((acc, r) => acc + r.sessions, 0),
    [perDevice],
  );

  // d3-shape's pie generator handles the angle math; we project our
  // canonical-order array onto it (skipping zero-session devices so a
  // Desktop-only property doesn't render a degenerate hairline arc).
  const wedges = useMemo(() => {
    if (totalSessions === 0) return [];
    const lookup = new Map(perDevice.map((r) => [r.device, r.sessions] as const));
    const data = DEVICE_RENDER_ORDER.flatMap<{ device: DeviceKey; sessions: number }>((d) => {
      const sessions = lookup.get(d) ?? 0;
      return sessions > 0 ? [{ device: d, sessions }] : [];
    });
    const pie = d3Pie<{ device: DeviceKey; sessions: number }>()
      .value((d) => d.sessions)
      .sort(null);
    return pie(data);
  }, [perDevice, totalSessions]);

  const outerR = size / 2 - 4;
  const innerR = outerR * 0.62;
  const arcGen = useMemo(
    () => d3Arc<{ startAngle: number; endAngle: number }>().innerRadius(innerR).outerRadius(outerR),
    [innerR, outerR],
  );

  if (totalSessions === 0) {
    return (
      <svg
        width={size}
        height={size}
        viewBox={`0 0 ${size} ${size}`}
        role="img"
        aria-label={`${ariaLabel}. No sessions in this period.`}
        className="font-sans"
      >
        <circle
          cx={size / 2}
          cy={size / 2}
          r={outerR}
          fill="none"
          stroke="#E5E7EB"
          strokeWidth={2}
        />
        <text x={size / 2} y={size / 2 + 3} fontSize={10} fill="#6B7280" textAnchor="middle">
          No data
        </text>
      </svg>
    );
  }

  return (
    <svg
      width={size}
      height={size}
      viewBox={`0 0 ${size} ${size}`}
      role="img"
      aria-label={ariaLabel}
      className="font-sans"
    >
      <g transform={`translate(${size / 2}, ${size / 2})`}>
        {wedges.map((w) => {
          const d = arcGen({ startAngle: w.startAngle, endAngle: w.endAngle });
          if (!d) return null;
          return (
            <path
              key={w.data.device}
              d={d}
              fill={DEVICE_COLOR[w.data.device]}
              stroke="#ffffff"
              strokeWidth={1}
            />
          );
        })}
      </g>
    </svg>
  );
}

// ---------------------------------------------------------------------------
// 100% stacked area — per-day device mix
// ---------------------------------------------------------------------------

export interface MobileTrendRow {
  date: string;
  device: DeviceKey;
  sessions: number;
}

export interface MobileAreaChartProps {
  rows: ReadonlyArray<MobileTrendRow>;
  granularity: "day" | "week" | "month";
  ariaLabel?: string;
}

const AREA_HEIGHT = 220;
const MARGIN = { top: 18, right: 12, bottom: 28, left: 40 };
const AXIS_COLOR = "#6B7280";
const GRID_COLOR = "#E5E7EB";

export function MobileAreaChart({
  rows,
  granularity,
  ariaLabel = "Device mix as a 100% stacked area chart",
}: MobileAreaChartProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [width, setWidth] = useState(640);
  const [hoverX, setHoverX] = useState<number | null>(null);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const el = containerRef.current;
    if (!el) return;
    const ro = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const w = entry.contentRect.width;
        if (w > 0) setWidth(Math.max(280, Math.floor(w)));
      }
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const innerW = Math.max(10, width - MARGIN.left - MARGIN.right);
  const innerH = AREA_HEIGHT - MARGIN.top - MARGIN.bottom;

  // Bucket the rows into a per-date matrix in canonical device order.
  // Devices missing on a given day get 0 so d3-stack still produces a
  // continuous area band.
  const { presentDevices, byDate } = useMemo(() => {
    const dateSet = new Set<string>();
    for (const r of rows) dateSet.add(r.date);
    const dates = Array.from(dateSet).sort();
    interface Row {
      date: string;
      cells: Record<DeviceKey, number>;
    }
    const out: Row[] = dates.map((d) => ({
      date: d,
      cells: { desktop: 0, mobile: 0, tablet: 0, other: 0 },
    }));
    const idx = new Map(out.map((r, i) => [r.date, i]));
    for (const r of rows) {
      const i = idx.get(r.date);
      if (i === undefined) continue;
      out[i].cells[r.device] = (out[i].cells[r.device] ?? 0) + r.sessions;
    }
    const present: DeviceKey[] = DEVICE_RENDER_ORDER.filter((d) =>
      out.some((row) => row.cells[d] > 0),
    );
    return { presentDevices: present, byDate: out };
  }, [rows]);

  const flatCells = useMemo(() => byDate.map((r) => ({ ...r.cells })), [byDate]);

  const { stacked, xScale, yScale, xTicks } = useMemo(() => {
    if (byDate.length === 0 || presentDevices.length === 0) {
      return {
        stacked: [],
        xScale: scaleTime().range([0, innerW]),
        yScale: scaleLinear().range([innerH, 0]),
        xTicks: [] as Date[],
      };
    }
    const stackGen = d3Stack<Record<DeviceKey, number>>()
      .keys(presentDevices)
      .offset(stackOffsetExpand);
    const stacked = stackGen(flatCells);
    const dateObjs = byDate.map((d) => new Date(`${d.date}T00:00:00Z`));
    const [xMin, xMax] = extent(dateObjs) as [Date, Date];
    const yMaxRaw = max(stacked, (s) => max(s, (d) => d[1])) ?? 1;
    const xScale = scaleTime().domain([xMin, xMax]).range([0, innerW]);
    const yScale = scaleLinear().domain([0, yMaxRaw]).range([innerH, 0]);
    const tickCount = granularity === "day" ? 5 : granularity === "week" ? 4 : 3;
    return { stacked, xScale, yScale, xTicks: xScale.ticks(tickCount) };
  }, [byDate, flatCells, presentDevices, innerW, innerH, granularity]);

  const areaGen = useMemo(() => {
    return d3Area<SeriesPoint<Record<DeviceKey, number>>>()
      .x((_, i) => (byDate[i] ? xScale(new Date(`${byDate[i].date}T00:00:00Z`)) : 0))
      .y0((d) => yScale(d[0]))
      .y1((d) => yScale(d[1]));
  }, [xScale, yScale, byDate]);

  const yTickPercents = [0, 25, 50, 75, 100];

  // Hover crosshair + tooltip, matching the SourceMixChart pattern.
  const dateObjs = useMemo(
    () => byDate.map((d) => ({ date: d.date, dateObj: new Date(`${d.date}T00:00:00Z`) })),
    [byDate],
  );

  const onMouseMove = (event: React.MouseEvent<SVGRectElement>) => {
    const svg = event.currentTarget.ownerSVGElement;
    if (!svg) return;
    const rect = svg.getBoundingClientRect();
    const xInside = event.clientX - rect.left - MARGIN.left;
    if (xInside < 0 || xInside > innerW) {
      setHoverX(null);
      return;
    }
    setHoverX(xInside);
  };

  const hoverInfo = useMemo(() => {
    if (hoverX === null || dateObjs.length === 0) return null;
    const dateAtX = xScale.invert(hoverX);
    // Pick the closest date to the cursor. We do a linear scan over
    // dateObjs (small N — at most ~120 buckets) so we don't pull a d3
    // bisector dep just for this.
    let best = 0;
    let bestDist = Infinity;
    for (let i = 0; i < dateObjs.length; i++) {
      const dist = Math.abs(dateObjs[i].dateObj.getTime() - dateAtX.getTime());
      if (dist < bestDist) {
        best = i;
        bestDist = dist;
      }
    }
    const ref = dateObjs[best];
    const cellRow = byDate[best];
    if (!cellRow) return null;
    const cx = xScale(ref.dateObj);
    const total = sum(presentDevices, (k) => cellRow.cells[k] ?? 0);
    const series = presentDevices.map((k) => {
      const v = cellRow.cells[k] ?? 0;
      return { device: k, value: v, share: total > 0 ? v / total : 0 };
    });
    return { cx, date: ref.date, series, total };
  }, [hoverX, dateObjs, xScale, byDate, presentDevices]);

  if (rows.length === 0 || byDate.length === 0 || presentDevices.length === 0) {
    return (
      <div
        ref={containerRef}
        className="flex h-56 items-center justify-center rounded-xl bg-neutral-50 text-sm text-neutral-500"
      >
        No traffic in the selected window.
      </div>
    );
  }

  return (
    <div ref={containerRef} className="relative w-full">
      <svg
        width={width}
        height={AREA_HEIGHT}
        viewBox={`0 0 ${width} ${AREA_HEIGHT}`}
        role="img"
        aria-label={ariaLabel}
        className="ts-chart-reveal font-sans"
      >
        <g transform={`translate(${MARGIN.left}, ${MARGIN.top})`}>
          {yTickPercents.map((pct) => {
            const y = yScale(pct / 100);
            return (
              <g key={`y-${pct}`}>
                <line x1={0} y1={y} x2={innerW} y2={y} stroke={GRID_COLOR} />
                <text x={-8} y={y + 3} fontSize={10} fill={AXIS_COLOR} textAnchor="end">
                  {pct}%
                </text>
              </g>
            );
          })}
          {xTicks.map((d, i) => (
            <g key={`x-${i}`}>
              <line x1={xScale(d)} y1={innerH} x2={xScale(d)} y2={innerH + 4} stroke={AXIS_COLOR} />
              <text
                x={xScale(d)}
                y={innerH + 16}
                fontSize={10}
                fill={AXIS_COLOR}
                textAnchor="middle"
              >
                {formatXTick(d, granularity)}
              </text>
            </g>
          ))}
          {stacked.map((s) => {
            const d = areaGen(s);
            if (!d) return null;
            const fill = DEVICE_COLOR[s.key as DeviceKey];
            return (
              <path
                key={s.key}
                d={d}
                fill={fill}
                stroke="#ffffff"
                strokeWidth={0.5}
                opacity={0.92}
              />
            );
          })}
          {hoverInfo ? (
            <line
              x1={hoverInfo.cx}
              y1={0}
              x2={hoverInfo.cx}
              y2={innerH}
              stroke={AXIS_COLOR}
              strokeDasharray="3 3"
              strokeWidth={1}
            />
          ) : null}
          <rect
            x={0}
            y={0}
            width={innerW}
            height={innerH}
            fill="transparent"
            onMouseMove={onMouseMove}
            onMouseLeave={() => setHoverX(null)}
          />
        </g>
      </svg>

      {hoverInfo ? (
        <div
          className="pointer-events-none absolute -translate-x-1/2 rounded-lg border border-neutral-200 bg-white px-3 py-2 text-xs shadow-sm"
          style={{
            left: `${hoverInfo.cx + MARGIN.left}px`,
            top: `${MARGIN.top}px`,
            transform: `translate(-50%, -110%)`,
          }}
        >
          <p className="font-semibold text-neutral-950">{formatTooltipDate(hoverInfo.date)}</p>
          <ul className="mt-1 space-y-0.5">
            {hoverInfo.series
              .filter((s) => s.value > 0)
              .sort((a, b) => b.value - a.value)
              .map((s) => (
                <li key={s.device} className="flex items-center gap-2 whitespace-nowrap">
                  <span
                    className="inline-block h-2 w-2 rounded-full"
                    style={{ backgroundColor: DEVICE_COLOR[s.device] }}
                    aria-hidden="true"
                  />
                  <span className="text-neutral-600">{DEVICE_LABEL[s.device]}</span>
                  <span className="ml-auto font-semibold tabular-nums text-neutral-950">
                    {(s.share * 100).toFixed(1)}%
                  </span>
                  <span className="ml-1 tabular-nums text-neutral-500">
                    ({compactNumber(s.value)})
                  </span>
                </li>
              ))}
          </ul>
        </div>
      ) : null}
    </div>
  );
}

function formatXTick(d: Date, granularity: "day" | "week" | "month"): string {
  const month = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];
  if (granularity === "month") {
    return `${month[d.getUTCMonth()]} ${String(d.getUTCFullYear()).slice(2)}`;
  }
  return `${month[d.getUTCMonth()]} ${d.getUTCDate()}`;
}

function formatTooltipDate(iso: string): string {
  try {
    const d = new Date(`${iso}T00:00:00Z`);
    return d.toLocaleDateString(undefined, {
      weekday: "short",
      month: "short",
      day: "numeric",
      timeZone: "UTC",
    });
  } catch {
    return iso;
  }
}

function compactNumber(n: number): string {
  if (!isFinite(n)) return "—";
  if (Math.abs(n) >= 10_000) {
    return new Intl.NumberFormat("en", { notation: "compact", maximumFractionDigits: 1 }).format(n);
  }
  return new Intl.NumberFormat("en").format(Math.round(n));
}
